import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { Card, CardBody, CardTitle, Row, Col, Spinner } from "reactstrap";
import { getCollectionWithProducts } from "./collectionsService";
import { STORE_MISSING_IMAGE_URL } from "../constants";
import "../catalog/ProductsList.css";
import PriceOrLogin from "./components/PriceOrLogin";
import { maybeAutoLoginFromQuery } from "./autoLogin";
import { AppContext } from "../context";

const STORE_PRODUCTS_BASE = "/products";

const Collection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { identifier } = useParams();
  const { setCurrentUser } = useContext(AppContext) || {};

  const [collection, setCollection] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    maybeAutoLoginFromQuery({ location, navigate, setCurrentUser });
  }, [location, navigate, setCurrentUser]);

  useEffect(() => {
    const fetchCollection = async () => {
      if (!identifier) return;
      try {
        setLoading(true);
        setError(null);
        const { collection: col, products: list } =
          await getCollectionWithProducts(identifier);
        setCollection(col || null);
        setProducts(Array.isArray(list) ? list : []);
      } catch (err) {
        setError(
          err?.error ||
            err?.message ||
            "Failed to load collection. Please try again."
        );
        setCollection(null);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCollection();
  }, [identifier]);

  const getMainImage = (product) => {
    const imgs = product?.images;
    if (Array.isArray(imgs) && imgs.length > 0) return imgs[0];
    if (product?.image) return product.image;
    return null;
  };

  if (loading) {
    return (
      <div className="catalog-products-list-container">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner color="primary" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="catalog-products-list-container">
        <div className="container pt-4">
          <h2 className="mb-2 d-flex align-items-center gap-2 mx-2">
            <Link to="../" relative="path" className="text-muted text-decoration-none fw-bold">
              Products
            </Link>
            <span className="text-muted fw-light">›</span>
            <span>{collection.name}</span>
          </h2>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

            {/* Products grid */}
            {products.length === 0 && !loading ? (
              <div className="text-center py-5">
                <p className="text-muted">No products in this collection.</p>
              </div>
            ) : (
              <Row className="g-3">
                {products.map((product, index) => {
                  const img = getMainImage(product);
                  const imgStyle = img && img.includes && img.includes("-ld-");
                  return (
                    <Col
                      key={product.id ?? product.product_id ?? index}
                      xs={12}
                      sm={12}
                      md={6}
                      lg={4}
                    >
                      <Card
                        className="h-100 shadow-sm catalog-product-card"
                        style={{
                          cursor: "pointer",
                          backgroundColor: "transparent",
                          overflow: "hidden",
                          border: "1px solid #DDDDDD",
                        }}
                        onClick={() =>
                          navigate(`${STORE_PRODUCTS_BASE}/${product.id}`, {
                            state: {
                              from: `${location.pathname}${location.search || ""}`,
                            },
                          })
                        }
                      >
                        <div
                          className="position-relative mb-2 d-flex justify-content-center align-items-center"
                          style={{
                            width: "100%",
                            height: "200px",
                            overflow: "hidden",
                            padding: "0.5rem",
                            backgroundSize: "50%",
                            backgroundRepeat: "repeat",
                            backgroundPosition: "center",
                          }}
                        >
                          {img ? (
                            <img
                              src={img}
                              alt={product.name}
                              style={{
                                width: "auto",
                                objectFit: "contain",
                                maxWidth: "100%",
                                maxHeight: "100%",
                                height: "auto",
                              }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = STORE_MISSING_IMAGE_URL;
                              }}
                            />
                          ) : (
                            <i className="fa fa-image fa-3x text-muted" />
                          )}
                        </div>
                        <CardBody className="d-flex flex-column p-0 p-lg-2">
                          <CardTitle
                            className="mb-1 fw-semibold text-dark px-2 pt-2"
                            style={{ fontSize: "0.75rem", lineHeight: "1.2" }}
                          >
                            {product.name}
                          </CardTitle>
                          {product.sku && (
                            <p className="card-text text-muted small mb-0 px-2">
                              SKU: {product.sku}
                            </p>
                          )}
                          <div className="mt-2 pt-2 mx-2 mb-2">
                            <PriceOrLogin
                              price={product.discounted_price ?? product.price}
                              strikethroughPrice={
                                product.discounted_price
                                  ? product.price
                                  : undefined
                              }
                              className="fw-semibold"
                            />
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
        </div>
      </div>
    </>
  );
};

export default Collection;
